import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import DataTable, { downloadBlob } from "@/components/DataTable";
import CommercialStatusBadge from "@/components/CommercialStatusBadge";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";

export default function InquiryList() {
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);

  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)); }, []);

  const fetchData = React.useCallback(async ({ skip, limit, sort, search, filters }) => {
    const params = { skip, limit, sort, search };
    if (filters.status) params.status = filters.status;
    if (filters.customer_id) params.customer_id = filters.customer_id;
    const { data } = await api.get("/inquiries", { params });
    return data;
  }, []);

  async function handleExport() {
    const token = localStorage.getItem("hispl_token");
    const res = await fetch(`${API}/inquiries/export`, { headers: { Authorization: `Bearer ${token}` } });
    await downloadBlob(res, "inquiries.xlsx");
  }

  const columns = [
    { key: "inquiry_number", label: "Inquiry #", sortable: true,
      render: (r) => <span className="font-mono text-[12px] font-semibold text-blue-900">{r.inquiry_number}</span> },
    { key: "inquiry_date", label: "Date", sortable: true, className: "font-mono text-xs text-slate-600" },
    { key: "customer_name", label: "Customer", sortable: false, className: "text-slate-800" },
    { key: "plant_name", label: "Plant", sortable: false, className: "text-slate-600 text-xs" },
    { key: "source", label: "Source", visibleByDefault: true,
      render: (r) => <span className="text-xs text-slate-600">{r.source}</span> },
    { key: "line_count", label: "Lines",
      render: (r) => <span className="font-mono text-xs">{r.line_count}</span> },
    { key: "total_qty", label: "Total Qty",
      render: (r) => <span className="font-mono text-xs font-semibold">{r.total_qty}</span> },
    { key: "expected_delivery", label: "Expected", visibleByDefault: false,
      render: (r) => <span className="font-mono text-xs text-slate-600">{r.expected_delivery || "—"}</span> },
    { key: "status", label: "Status", sortable: true,
      render: (r) => <CommercialStatusBadge status={r.status} /> },
    { key: "created_by_name", label: "Created By", visibleByDefault: false,
      render: (r) => <span className="text-xs text-slate-600">{r.created_by_name}</span> },
  ];

  return (
    <div data-testid="inquiry-list-page" className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">Commercial Workflow · Stage 1</div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>Inquiries</h1>
          <p className="text-sm text-slate-500 mt-1">Customer inquiries received — the entry point to the commercial workflow.</p>
        </div>
        <Button
          data-testid="new-inquiry-btn"
          className="bg-blue-900 hover:bg-blue-800 text-white"
          onClick={() => nav("/inquiries/new")}
        >
          <Plus className="w-4 h-4 mr-1" /> New Inquiry
        </Button>
      </div>

      <DataTable
        columns={columns}
        fetchData={fetchData}
        onExport={handleExport}
        filters={[
          { key: "status", label: "Status",
            options: ["Open", "Quoted", "Lost", "Converted"].map((s) => ({ value: s, label: s })) },
          { key: "customer_id", label: "Customer",
            options: customers.map((c) => ({ value: c.id, label: c.name })) },
        ]}
        storageKey="inquiries"
        onRowClick={(r) => nav(`/inquiries/${r.id}`)}
        emptyMessage="No inquiries yet. Click 'New Inquiry' to create one."
      />
    </div>
  );
}
